import React, { FunctionComponent, useCallback, useEffect, useState } from "react"
import { useLazyQuery } from "@apollo/client"
import moment from "moment"
import { CircularProgress, Paper, Divider, Typography } from "@material-ui/core"
import { ReportsTable } from "./ReportsTable"
import styles from "./ReportsPage.module.scss"
import { USAGE_REPORT } from "../../queries/USAGE_REPORT"
import { ReportsPageToolbar } from "./ReportsPageToolbar"
import { useNotification } from "../../common/hooks/useNotification"
import { OperationsProgressCard } from "./OperationsProgressCard"
import { StorageProgressCard } from "./StorageProgressCard"
import { Title } from "../../components/Title"

export const ReportsPage: FunctionComponent = () => {
    const { notifyError } = useNotification()
    const [selectedDate, setDate] = useState<moment.Moment>(moment())

    const getMonth = (date: moment.Moment) => {
        return date.month() + 1 // moment month is zero based, sql is not
    }

    const getYear = (date: moment.Moment) => {
        return date.year()
    }

    const isCurrentMonth = useCallback(() => {
        return moment().month() + 1 === getMonth(selectedDate) && isCurrentYear()
    }, [selectedDate, isCurrentYear])

    const [
        getUsageReports,
        { data: reportsData, loading: reportsLoading, error }
    ] = useLazyQuery(USAGE_REPORT, { fetchPolicy: "network-only" })

    const onDateChange = useCallback(
        (date: moment.Moment) => {
            setDate(date)
            getUsageReports({
                variables: {
                    date: {
                        month: getMonth(date),
                        year: getYear(date)
                    }
                }
            })
        },
        [getUsageReports]
    )

    useEffect(() => {
        if (error) {
            notifyError("Error retrieving usage report")
        }
    }, [error])

    function isCurrentYear() {
        return new Date().getFullYear() === getYear(selectedDate)
    }

    return (
        <>
            <Title text={"Reports"} />
            <div className={styles.ReportsPage}>
                <ReportsPageToolbar updateDate={onDateChange} />
                {reportsLoading && <CircularProgress data-testid="reports-loading" />}
                {reportsData && (
                    <>
                        <Paper className={styles.ReportsPage__table}>
                            <ReportsTable reports={reportsData.whoAmI.company.users} />
                        </Paper>
                        <Paper className={styles.ReportsPage__usage}>
                            <OperationsProgressCard
                                maxOperations={reportsData.whoAmI.company.maxOperations}
                                currentOperations={reportsData.whoAmI.company.currentOperations}
                                isCurrentMonth={isCurrentMonth()}
                            />
                        </Paper>
                        <Divider className={styles.ReportsPage__divider} />
                        <Typography
                            className={styles.ReportsPage__storageTitle}
                            data-testid="storage-report-title"
                            variant="h6"
                        >
                            Storage Report
                        </Typography>
                        <Paper className={styles.ReportsPage__usage}>
                            <StorageProgressCard
                                maxStorage={reportsData.whoAmI.company.maxStorage}
                                currentStorage={reportsData.whoAmI.company.currentStorage}
                                isCurrentYear={isCurrentYear()}
                            />
                        </Paper>
                    </>
                )}
            </div>
        </>
    )
}
